import { makeStyles } from "@material-ui/core/styles";
import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    FaAngleRight,
    FaCogs,
    FaInfoCircle,
    FaKey,
    FaPallet,
    FaPen,
    FaPowerOff,
} from "react-icons/fa";
import { IoHomeSharp } from "react-icons/io5";
import {
    Menu,
    MenuItem,
    ProSidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SubMenu,
} from "react-pro-sidebar";
import { Link, useHistory } from "react-router-dom";
import { clearAuthenticatedSession } from "../../configurations/authSession";
import { useTheme } from "../../configurations/themeProvider";
import menuService from "../service/menuService";
import MenuTitle from "./components/MenuTitle";
import { NewVersionDialog } from "./components/VersionDialog/NewVersionDialog";
import "./scss/styles.scss";

const useStyles = makeStyles(() => ({
    icon: {
        height: 20,
        width: 20,
    },
    link: {
        borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
        paddingTop: 8,
    },
}));

const Sidebar = React.forwardRef((props, ref) => {
    const firstAccess = sessionStorage.getItem("firstAccess") === "1";
    const { t } = useTranslation();
    const history = useHistory();
    const { resetClientTheme } = useTheme();
    const [toggled, setToggled] = useState(false);
    const classes = useStyles();
    const [collapsed, setCollapsed] = useState(true);
    const [menus, setMenus] = useState([]);
    const [access, setAccess] = useState();
    const [registration, setRegistration] = useState();
    const [maintenance, setMaintenance] = useState();
    const [operation, setOperation] = useState();
    const [materialPicking, setMaterialPicking] = useState();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const userId = sessionStorage.getItem("userId");
        menuService
            .getMenu({ loggedUserId: userId })
            .then((response) => {
                if (response.status === 200) {
                    setMenus(response.data);
                }
            })
            .catch(() => { });
    }, []);

    useEffect(() => {
        menus.forEach((menu) => {
            switch (menu.translationString) {
                case "access":
                    setAccess(menu);
                    break;
                case "registration":
                    setRegistration(menu);
                    break;
                case "maintenance":
                    setMaintenance(menu);
                    break;
                case "operational":
                    setOperation(menu);
                    break;
                case "materialPicking":
                    setMaterialPicking(menu);
                    break;
                default:
                    break;
            }
        });
    }, [menus]);

    const createLink = (item, isLast) => (
        <MenuItem key={item.subMenuId}>
            <Link to={item.link}>{t(item.translationString)}</Link>
            {!isLast ? <div className={classes.link} /> : null}
        </MenuItem>
    );

    const handleLogout = () => {
        clearAuthenticatedSession();
        resetClientTheme();
        history.replace("/");
    };

    return (
        <div
            id="sidebar"
            style={{
                zIndex: 9999,
                position: "fixed",
            }}
        >
            <ProSidebar
                image={false}
                rtl={false}
                collapsed={collapsed}
                toggled={toggled}
                onToggle={(value) => setToggled(value)}
            >
                <SidebarHeader>
                    <div style={{ marginTop: "10px" }}>
                        <Menu>
                            <MenuItem
                                icon={<FaAngleRight className="arrow-toggle-collapse" />}
                                onClick={() => setCollapsed(!collapsed)}
                            >
                                {t("collapse")}
                            </MenuItem>
                        </Menu>
                        <Menu>
                            <MenuItem
                                icon={<IoHomeSharp className={classes.icon} />}
                                onClick={() => history.push("/home")}
                            >
                                {t("home")}
                            </MenuItem>
                        </Menu>
                    </div>
                </SidebarHeader>

                <SidebarContent>
                    {access?.subMenus && (
                        <Menu>
                            <SubMenu title={t("access")} icon={<FaKey className={classes.icon} />}>
                                {collapsed ? <MenuTitle title={access.translationString} /> : null}
                                {access.subMenus.map((item, index, array) => createLink(item, array.length - 1 === index))}
                            </SubMenu>
                        </Menu>
                    )}
                    {!firstAccess && (
                        <>
                            {maintenance?.subMenus && (
                                <Menu>
                                    <SubMenu title={t("maintenance")} icon={<FaPallet className={classes.icon} />}>
                                        {collapsed ? <MenuTitle title={maintenance.translationString} /> : null}
                                        {maintenance.subMenus.map((item, index, array) => createLink(item, array.length - 1 === index))}
                                    </SubMenu>
                                </Menu>
                            )}

                            {registration?.subMenus && (
                                <Menu>
                                    <SubMenu title={t("registration")} icon={<FaPen className={classes.icon} />}>
                                        {collapsed ? <MenuTitle title={registration.translationString} /> : null}
                                        {registration.subMenus.map((item, index, array) => createLink(item, array.length - 1 === index))}
                                    </SubMenu>
                                </Menu>
                            )}

                            {operation?.subMenus && (
                                <Menu>
                                    <SubMenu title={t("operational")} icon={<FaCogs className={classes.icon} />}>
                                        {collapsed ? <MenuTitle title={operation.translationString} /> : null}
                                        {operation.subMenus.map((item, index, array) => createLink(item, array.length - 1 === index))}
                                    </SubMenu>
                                </Menu>
                            )}
                            {materialPicking?.subMenus && (
                                <Menu>
                                    <SubMenu title={t("materialPicking")} icon={<FaPallet className={classes.icon} />}>
                                        {collapsed ? <MenuTitle title={materialPicking.translationString} /> : null}
                                        {materialPicking.subMenus.map((item, index, array) => createLink(item, array.length - 1 === index))}
                                    </SubMenu>
                                </Menu>
                            )}
                        </>
                    )}
                </SidebarContent>
                <SidebarFooter>
                    <Menu>
                        <MenuItem icon={<FaInfoCircle className={classes.icon} />} onClick={() => setOpen(true)}>
                            {t("info")}
                        </MenuItem>
                    </Menu>
                    <Menu>
                        <MenuItem icon={<FaPowerOff className={classes.icon} />} onClick={handleLogout}>
                            {t("logout")}
                        </MenuItem>
                    </Menu>
                </SidebarFooter>
            </ProSidebar>

            {open ? <NewVersionDialog dialog={() => setOpen(false)} dialogOpen={open} /> : null}
        </div>
    );
});

export default Sidebar;